import { useState, FormEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { publicAPI } from '../services/api'
import {
  BarChart3,
  CheckCircle,
  AlertCircle,
  Building2,
  User,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react'

// Blocklist of generic email providers - only corporate emails allowed
const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.no',
  'outlook.com',
  'outlook.no',
  'live.com',
  'live.no',
  'yahoo.com',
  'yahoo.no',
  'aol.com',
  'icloud.com',
  'me.com',
  'protonmail.com',
  'proton.me',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'yandex.com',
  'zoho.com',
  'tutanota.com',
  'fastmail.com',
  'mail.ru',
  'inbox.com',
  'ymail.com'
]

interface GACContent {
  id: number
  Title: string
  Content: string
  ContentType: string
  IsActive: number
  ModifiedTime: string
}

export default function SubscriptionSignup() {
  const { t } = useTranslation('account')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [gacAnnouncementId, setGacAnnouncementId] = useState<number | null>(null)

  const { register, logout } = useAuth()

  // Fetch the active general terms on mount; registration records its id
  useEffect(() => {
    fetchContent()
  }, [])

  const fetchContent = async () => {
    try {
      const gacRes = await publicAPI.getAnnouncements({ content_type: 'generalterms' })
      const activeGac = gacRes.data.data?.find((ann: GACContent) => ann.IsActive === 1)
      if (activeGac) {
        setGacAnnouncementId(activeGac.id)
      }
    } catch (err) {
      console.error('Failed to fetch general terms:', err)
    }
  }

  // Password strength calculation
  const checkPasswordStrength = (pass: string): number => {
    let strength = 0
    if (pass.length >= 8) strength++
    if (pass.length >= 12) strength++
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength++
    if (/\d/.test(pass)) strength++
    if (/[^a-zA-Z0-9]/.test(pass)) strength++
    return strength
  }

  const passwordStrength = checkPasswordStrength(password)
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-red-500'
    if (passwordStrength <= 2) return 'bg-yellow-500'
    if (passwordStrength <= 3) return 'bg-blue-500'
    return 'bg-green-500'
  }

  const getStrengthText = () => {
    if (passwordStrength <= 1) return t('subscription.strength.weak')
    if (passwordStrength <= 2) return t('subscription.strength.medium')
    if (passwordStrength <= 3) return t('subscription.strength.good')
    return t('subscription.strength.strong')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    // Block non-corporate emails
    const emailDomain = email.split('@')[1]?.toLowerCase()
    if (!emailDomain || BLOCKED_EMAIL_DOMAINS.includes(emailDomain)) {
      setError(t('subscription.errors.corporateOnly'))
      return
    }

    if (password.length < 8) {
      setError(t('subscription.errors.passwordTooShort'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('subscription.errors.passwordMismatch'))
      return
    }

    if (!acceptedTerms) {
      setError(t('subscription.errors.mustAcceptTerms'))
      return
    }

    setLoading(true)
    try {
      const result = await register(
        email,
        password,
        username || undefined,
        gacAnnouncementId || undefined,
        phone || undefined,
        companyName || undefined
      )
      if (result.success) {
        // Logout since account needs admin approval before access
        await logout()
        setSuccess(true)
      } else {
        setError(result.error || t('subscription.errors.failed'))
      }
    } catch (err: any) {
      setError(err.message || t('subscription.errors.unexpected'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('subscription.success.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('subscription.success.received')}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('subscription.success.emailWhenApproved')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              {t('subscription.success.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <BarChart3 className="w-10 h-10 text-primary-500" />
            <span className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              bilpriser.no <span className="text-primary-500">PROFF</span>
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('subscription.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('subscription.trialInfo')}
          </p>
        </div>

        {/* Registration Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t('subscription.formTitle')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('subscription.formIntro')}
            </p>

            {/* Trial info banner */}
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('subscription.trialInfo')}
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Navn (username) */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <User className="h-4 w-4 inline mr-1" />
                  {t('subscription.name')}
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('subscription.namePlaceholder')}
                  required
                />
              </div>

              {/* E-post */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Mail className="h-4 w-4 inline mr-1" />
                  {t('subscription.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('subscription.emailPlaceholder')}
                  required
                />
              </div>

              {/* Telefon */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Phone className="h-4 w-4 inline mr-1" />
                  {t('subscription.phone')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('subscription.phonePlaceholder')}
                />
              </div>

              {/* Firmanavn (optional) */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Building2 className="h-4 w-4 inline mr-1" />
                  {t('subscription.company')}
                </label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('subscription.companyPlaceholder')}
                />
              </div>

              {/* Passord */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Lock className="h-4 w-4 inline mr-1" />
                  {t('subscription.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('subscription.passwordPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600 dark:text-gray-400">{t('subscription.strengthLabel')}</span>
                      <span className={`font-medium ${
                        passwordStrength <= 1 ? 'text-red-600' :
                        passwordStrength <= 2 ? 'text-yellow-600' :
                        passwordStrength <= 3 ? 'text-blue-600' :
                        'text-green-600'
                      }`}>
                        {getStrengthText()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getStrengthColor()}`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bekreft passord */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Lock className="h-4 w-4 inline mr-1" />
                  {t('subscription.confirm')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('subscription.confirmPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                {/* Password Match Indicator */}
                {confirmPassword && (
                  <div className="mt-2 flex items-center text-sm">
                    {passwordsMatch ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-green-600 dark:text-green-400">{t('subscription.passwordsMatch')}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                        <span className="text-red-600 dark:text-red-400">{t('subscription.passwordsDontMatch')}</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Terms checkbox - links to /terms (generalterms) */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded-sm"
                />
                <label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300">
                  {t('subscription.acceptTermsPrefix')}{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 underline"
                  >
                    {t('subscription.termsLink')}
                  </Link>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !passwordsMatch || !acceptedTerms}
                className="w-full py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('subscription.submitting')}
                  </span>
                ) : (
                  t('subscription.submit')
                )}
              </button>
            </form>

            {/* Back to login */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {t('subscription.haveAccount')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
